// Catalog API methods — mixin applied to the ApiService prototype chain.
// Methods run with `this` bound to the ApiService instance, so this.request()
// and cross-domain calls resolve through the shared HttpClient base.
import { getStudentToken, clearStudentProof, studentDiscountWasRemoved } from '../studentDiscountStore';

const withCatalogApi = (Base) => class extends Base {
  // Course endpoints
  async getCourses() {
    return this.request('/courses');
  }

  async getStudents() {
    return this.request('/users/students');
  }

  async getStudentsByCourse(courseId) {
    return this.request(`/users/students/course/${courseId}`);
  }

  async getClassmatesByCourse(courseId) {
    return this.request(`/users/students/course/${courseId}/classmates`);
  }

  async getCoursesForStudents() {
    return this.request('/users/courses-for-students');
  }

  async getPublicCourses() {
    return this.request('/courses/catalog');
  }

  async getCoursePricing(courseId) {
    return this.publicRequest(`/payments/course/${courseId}/pricing`);
  }

  async updateCoursePricing(courseId, pricingData) {
    return this.request(`/payments/course/${courseId}/pricing`, {
      method: 'PUT',
      body: JSON.stringify(pricingData)
    });
  }

  // Payment methods
  async createPaymentIntent(paymentData) {
    return this.request('/payments/create-payment-intent-authenticated', {
      method: 'POST',
      body: JSON.stringify(paymentData)
    });
  }


  // Guest checkout (account is created only after payment succeeds). These are
  // unauthenticated, so they use publicRequest; a 409 with errorData.requiresLogin
  // means the email already has an account.
  async getGuestCatalog() {
    return this.publicRequest('/payments/guest/catalog');
  }

  // Active multi-course discount rules (public), for the catalog's bundle offer.
  async getPublicBulkRules() {
    return this.publicRequest('/payments/guest/bulk-rules');
  }

  // New-student email check: a code is emailed, then exchanged for the token the guest payment
  // endpoints require. (The server answers the same whether or not a code was actually sent.)
  async guestRequestEmailCode(email) {
    return this.publicRequest('/payments/guest/email/request', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });
  }

  async guestVerifyEmailCode(email, code) {
    return this.publicRequest('/payments/guest/email/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code })
    });
  }

  async getGuestProducts() {
    return this.publicRequest('/payments/guest/products');
  }

  async getGuestProduct(id) {
    return this.publicRequest(`/payments/guest/products/${encodeURIComponent(id)}`);
  }

  async guestProductQuote(productIds, promoCode) {
    return this.publicRequest('/payments/guest/products/quote', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ productIds, promoCode: promoCode || null })
    });
  }

  async guestCreateProductPaymentIntent(data) {
    return this.publicRequest('/payments/guest/products/create-payment-intent', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
  }

  async guestQuote(courseIds, promoCode, skipBulk = false, studentToken = null) {
    return this.publicRequest('/payments/guest/quote', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ courseIds, promoCode: promoCode || null, skipBulk, studentToken })
    });
  }

  // Student discount. A signed-in student's calls carry their session (`authenticated`), so the server
  // links the .edu address to their account; a new student's calls are public and return a token.
  async getStudentDiscount(authenticated = false) {
    return authenticated
      ? this.request('/payments/student/discount')
      : this.publicRequest('/payments/student/discount');
  }

  async requestStudentCode(eduEmail, authenticated = false) {
    const options = { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ eduEmail }) };
    return authenticated
      ? this.request('/payments/student/request', options)
      : this.publicRequest('/payments/student/request', options);
  }

  async verifyStudentCode(eduEmail, code, authenticated = false) {
    const options = { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ eduEmail, code }) };
    return authenticated
      ? this.request('/payments/student/verify', options)
      : this.publicRequest('/payments/student/verify', options);
  }

  // A student who verified as a guest and then signed in: attach that proof to their account so the discount
  // follows them. Best effort and never throws. A proof the server turned down is dropped and the reason is
  // returned ({ failed, message }); a network failure keeps it for another try (null).
  async claimStudentProof() {
    const studentToken = getStudentToken();
    if (!studentToken) return null;
    try {
      const response = await this.request('/payments/student/claim', { method: 'POST', body: JSON.stringify({ studentToken }) });
      clearStudentProof();
      return response;
    } catch (error) {
      if (error?.status >= 400 && error?.status < 500) {
        clearStudentProof();
        // The server's reason (address taken, proof expired) so the student is told, not left wondering.
        return { failed: true, message: error.message || 'We could not apply your student discount.' };
      }
      return null;
    }
  }

  // True when this signed-in student is verified and the discount is on (the payment page then asks the
  // server for the discounted price even for a single course).
  async isStudentDiscountActive() {
    if (studentDiscountWasRemoved()) return false;
    try {
      const response = await this.getStudentDiscount(true);
      return Boolean(response?.enabled && response?.verified);
    } catch {
      return false;
    }
  }

  // Admin
  async getStudentDiscountSettings() {
    return this.request('/payments/student/settings');
  }

  async updateStudentDiscountSettings(settings) {
    return this.request('/payments/student/settings', { method: 'PUT', body: JSON.stringify(settings) });
  }

  async getVerifiedStudents(page = 1, pageSize = 25) {
    return this.request(`/payments/student/verified?page=${page}&pageSize=${pageSize}`);
  }

  async getStudentRedemptions(page = 1, pageSize = 25) {
    return this.request(`/payments/student/redemptions?page=${page}&pageSize=${pageSize}`);
  }

  async setStudentRevoked(eduEmail, revoked) {
    return this.request('/payments/student/verified', { method: 'PUT', body: JSON.stringify({ eduEmail, revoked }) });
  }

  async guestCreatePaymentIntent(data) {
    return this.publicRequest('/payments/guest/create-payment-intent', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
  }

  async guestCompleteCheckout(paymentIntentId, clientSecret) {
    return this.publicRequest('/payments/guest/complete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ paymentIntentId, clientSecret })
    });
  }

  async checkEnrollment(courseIds, email) {
    return this.publicRequest('/payments/check-enrollment', {
      method: 'POST',
      body: JSON.stringify({ courseIds, email })
    });
  }

  async confirmPayment(paymentData) {
    return this.request('/payments/confirm-payment', {
      method: 'POST',
      body: JSON.stringify(paymentData)
    });
  }

  // Promo codes API methods
  async getPromoCodes() {
    return this.request('/promo-codes');
  }

  async getActivePromoCodes() {
    return this.publicRequest('/promo-codes/active');
  }

  async validatePromoCode(code, courseIds = [], productIds = []) {
    // Use authenticated request since promo-codes routes require authentication
    // Users should be authenticated when on the payment form
    return this.request('/promo-codes/validate', {
      method: 'POST',
      body: JSON.stringify({ code, courseIds, productIds })
    });
  }

  async createPromoCode(promoData) {
    return this.request('/promo-codes', {
      method: 'POST',
      body: JSON.stringify(promoData)
    });
  }

  async updatePromoCode(id, promoData) {
    return this.request(`/promo-codes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(promoData)
    });
  }

  async deletePromoCode(id) {
    return this.request(`/promo-codes/${id}`, {
      method: 'DELETE'
    });
  }

  // Bulk pricing API methods
  async getBulkPricingRules() {
    return this.request('/bulk-pricing');
  }

  async getActiveBulkPricingRules() {
    return this.publicRequest('/bulk-pricing/active');
  }

  async calculateBulkPricing(courseIds) {
    // Use authenticated request since bulk-pricing routes require authentication
    // Users should be authenticated when on the payment form
    return this.request('/bulk-pricing/calculate', {
      method: 'POST',
      // A student who removed the discount is priced without it (the bundle offer, for example).
      body: JSON.stringify({ courseIds, ...(studentDiscountWasRemoved() ? { skipStudentDiscount: true } : {}) })
    });
  }

  async createBulkPricingRule(ruleData) {
    return this.request('/bulk-pricing', {
      method: 'POST',
      body: JSON.stringify(ruleData)
    });
  }

  async updateBulkPricingRule(id, ruleData) {
    return this.request(`/bulk-pricing/${id}`, {
      method: 'PUT',
      body: JSON.stringify(ruleData)
    });
  }

  async deleteBulkPricingRule(id) {
    return this.request(`/bulk-pricing/${id}`, {
      method: 'DELETE'
    });
  }

  // Additional products API methods
  async getAdditionalProducts() {
    return this.request('/additional-products');
  }

  async getActiveAdditionalProducts() {
    return this.request('/additional-products/active');
  }

  async getAdditionalProduct(id) {
    // FIX: Use authenticated request since the route requires authentication (global middleware)
    return this.request(`/additional-products/${id}`);
  }

  async createAdditionalProduct(productData) {
    return this.request('/additional-products', {
      method: 'POST',
      body: JSON.stringify(productData)
    });
  }

  async updateAdditionalProduct(id, productData) {
    return this.request(`/additional-products/${id}`, {
      method: 'PUT',
      body: JSON.stringify(productData)
    });
  }

  async deleteAdditionalProduct(id) {
    return this.request(`/additional-products/${id}`, {
      method: 'DELETE'
    });
  }

  async getProductCategories() {
    // FIX: Use authenticated request since the route requires authentication (global middleware)
    return this.request('/additional-products/categories/all');
  }

  async createProductCategory(categoryData) {
    return this.request('/additional-products/categories', {
      method: 'POST',
      body: JSON.stringify(categoryData)
    });
  }

  async getStudentSponsorships() {
    return this.request('/additional-products/sponsorships/all');
  }

  async getCourse(courseId) {
    return this.request(`/courses/${courseId}`);
  }

  async enrollInCourse(courseId) {
    return this.request(`/courses/${courseId}/enroll`, {
      method: 'POST'
    });
  }

  async assignStudentToCourse(courseId, studentId, status = 'active') {
    return this.request(`/courses/${courseId}/assign-student`, {
      method: 'POST',
      body: JSON.stringify({ studentId, status })
    });
  }

  async removeStudentFromCourse(courseId, studentId) {
    return this.request(`/courses/${courseId}/remove-student/${studentId}`, {
      method: 'DELETE'
    });
  }

  async getCourseProgress(courseId) {
    return this.request(`/courses/${courseId}/progress`);
  }

  // Elective endpoints
  async getElectives() {
    return this.request('/electives');
  }

  async getElective(electiveId) {
    return this.request(`/electives/${electiveId}`);
  }

  async enrollInElective(electiveId) {
    return this.request(`/electives/${electiveId}/enroll`, {
      method: 'POST'
    });
  }

  async assignStudentToElective(electiveId, studentId, status = 'active') {
    return this.request(`/electives/${electiveId}/assign-student`, {
      method: 'POST',
      body: JSON.stringify({ studentId, status })
    });
  }

  async removeStudentFromElective(electiveId, studentId) {
    return this.request(`/electives/${electiveId}/remove-student/${studentId}`, {
      method: 'DELETE'
    });
  }

  async createElective(electiveData) {
    return this.request('/electives', {
      method: 'POST',
      body: JSON.stringify(electiveData)
    });
  }

  async updateElective(electiveId, electiveData) {
    return this.request(`/electives/${electiveId}`, {
      method: 'PUT',
      body: JSON.stringify(electiveData)
    });
  }

  async deleteElective(electiveId) {
    return this.request(`/electives/${electiveId}`, {
      method: 'DELETE'
    });
  }

};

export default withCatalogApi;
